import React, { useState, useEffect } from 'react';
import { useCPRLog } from './CPRLog';
import { loadCurrentState, saveCurrentState } from './CprState/storage';
import { cprEventEmitter, EVENTS } from './cprEvents';
import AnionGapModal from './AnionGapModal';
import './SecondarySurvey.css';
import { useTranslation } from 'react-i18next';

interface SurveyLog {
  [key: string]: string;
}

const SecondarySurvey: React.FC = () => {
  const { addEntry } = useCPRLog();
  const { t } = useTranslation(['resus']);
  const [lastPerformed, setLastPerformed] = useState<SurveyLog>(() => {
    const savedState = loadCurrentState();
    return savedState?.secondarySurvey?.lastPerformed || {};
  });
  
  const [showAnionGapModal, setShowAnionGapModal] = useState(false);
  const [lastAnionGap, setLastAnionGap] = useState<string | null>(() => {
    const savedState = loadCurrentState();
    return savedState?.secondarySurvey?.lastAnionGap || null;
  });

  useEffect(() => {
    const unsubscribe = cprEventEmitter.subscribe(EVENTS.RESET_CPR, () => {
      setLastPerformed({});
      setLastAnionGap(null);
    });

    return () => unsubscribe();
  }, []);

  const tests = [
    { id: 'lab', label: t('secondarySurvey.labTest'), logText: t('secondarySurvey.logLabTest') },
    { id: 'pocus', label: 'POCUS/FAST-E', logText: t('secondarySurvey.logPocus') },
    { id: 'chest-xray', label: t('secondarySurvey.chestXRay'), logText: t('secondarySurvey.logChestXRay') },
    { id: 'ecg', label: t('secondarySurvey.ecg'), logText: t('secondarySurvey.logEcg') },
    { id: 'ct', label: 'CT', logText: t('secondarySurvey.logCt') },
  ];

  const handleTestClick = (test: { id: string; logText: string }) => {
    const timestamp = new Date().toISOString();
    
    // Update local state
    const newLastPerformed = { 
      ...lastPerformed, 
      [test.id]: timestamp 
    };
    setLastPerformed(newLastPerformed);

    // Save to storage
    saveCurrentState({
      secondarySurvey: {
        lastPerformed: newLastPerformed
      }
    }, 'secondarySurvey');

    addEntry({
      timestamp,
      text: test.logText,
      type: 'action',
      isImportant: false,
    });
  };

  const getAnionGapInterpretation = (gap: number) => {
    if (gap < 8) {
      return t('secondarySurvey.anionGapLow');
    } else if (gap >= 8 && gap < 12) {
      return t('secondarySurvey.anionGapNormal');
    } else {
      return t('secondarySurvey.anionGapElevated');
    }
  };

  const handleAnionGapSave = (gap: number) => {
    const timestamp = new Date().toISOString();
    
    // Add entry to log
    addEntry({
      timestamp,
      text: t('secondarySurvey.logAnionGap', { value: gap.toFixed(1) }),
      type: 'action',
      isImportant: false,
    });

    // Save to state
    const gapValue = `${gap.toFixed(1)}. ${getAnionGapInterpretation(gap)}`;
    setLastAnionGap(gapValue);
    
    saveCurrentState({
      secondarySurvey: {
        lastPerformed,
        lastAnionGap: gapValue
      }
    }, 'secondarySurvey');
    
    setShowAnionGapModal(false);
  };

  const renderTestButton = (test: any) => (
    <div key={test.id} className="test-button-container">
      <button 
        data-testid={`secondary-survey-${test.id}`}
        onClick={() => handleTestClick(test)}>
        {test.label}
      </button>
      {lastPerformed[test.id] && (
        <span className="last-performed">
          {t('secondarySurvey.lastPerformedAt')} {new Date(lastPerformed[test.id]).toLocaleTimeString('he-IL')}
        </span>
      )}
    </div>
  );

  return (
    <div className="secondary-survey">
      <h5>{t('secondarySurvey.clickToRecord')}:</h5>
      <div className="tests-grid">
        {tests.map(renderTestButton)}
      </div>
      
      <div className="anion-gap-section">
        <button 
          className="anion-gap-link" 
          data-testid="secondary-survey-anion-gap-link"
          onClick={() => setShowAnionGapModal(true)}
        >
          {t('secondarySurvey.anionGapCalculator')}
        </button>
        
        {lastAnionGap && (
          <div className="last-anion-gap">
            <span>{t('secondarySurvey.anionGapResult')}: {lastAnionGap}</span>
          </div>
        )}
      </div>
      
      <AnionGapModal
        isOpen={showAnionGapModal}
        onClose={() => setShowAnionGapModal(false)}
        onSave={handleAnionGapSave}
      />
    </div>
  );
};

export default SecondarySurvey;